import {
	GetLuckyWheelResponse,
	SpinLuckyWheelResponse,
} from '@/modules/promotion/type'
import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { Button } from '@/common/ui/button'
import useMutationSpinLuckyWheel from '@/modules/promotion/api/hooks/useMutationSpinLuckyWheel'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { motion } from 'framer-motion'
import LuckyWheelFallback from '../LuckyWheelFallback'
import { LuckyWheelRewardType } from '@/schema'

interface LuckyWheelDisplayProps {
	wheelData: GetLuckyWheelResponse
	disabled?: boolean
	onSpinComplete?: () => void
}

export default function LuckyWheelDisplay({
	wheelData,
	disabled,
	onSpinComplete,
}: LuckyWheelDisplayProps) {
	const segments = useMemo(() => {
		return wheelData.wheelSetting || []
	}, [wheelData.wheelSetting])

	const SHOW_LABELS = false

	const TOP_INDEX_AT_POINTER = 0
	const [isSpinning, setIsSpinning] = useState(false)
	const [rotationDeg, setRotationDeg] = useState(0)
	const [animationDurationSec, setAnimationDurationSec] = useState(3.5)
	const [animationDone, setAnimationDone] = useState(false)
	const [resultDone, setResultDone] = useState(false)

	const lastResultRef = useRef<{
		title: string
		amount: number
		type: string
		color?: string
	} | null>(null)

	const resetSpinState = useCallback(() => {
		setAnimationDone(false)
		setResultDone(false)
		lastResultRef.current = null
	}, [])

	const finalizeSpin = useCallback(() => {
		if (!isSpinning) return
		if (!(animationDone && resultDone)) return

		setIsSpinning(false)

		const result = lastResultRef.current
		if (result) {
			toast.success(`${result.title} (${result.amount})`)
		}

		onSpinComplete?.()
	}, [animationDone, isSpinning, onSpinComplete, resultDone])

	useEffect(() => {
		if (animationDone && resultDone) {
			finalizeSpin()
		}
	}, [animationDone, resultDone, finalizeSpin])

	const { mutate: spin, isPending } = useMutationSpinLuckyWheel({
		onSuccess: (data: SpinLuckyWheelResponse) => {
			const result = data.luckyWheelSpinResult
			if (result) {
				lastResultRef.current = {
					title: result.title,
					amount: result.amount,
					type: result.type || 'CREDIT',
					color: result.color,
				}

				const winnerIndex = segments.findIndex(
					(s) =>
						s.title === result.title &&
						s.amount === result.amount &&
						(s.type || LuckyWheelRewardType.CREDIT) ===
							(result.type || LuckyWheelRewardType.CREDIT),
				)

				if (segments.length > 0 && winnerIndex >= 0) {
					const total = segments.length
					const slice = 360 / total
					const targetCenter =
						slice * ((winnerIndex - TOP_INDEX_AT_POINTER + total) % total)
					const fullSpins = 4
					const current = ((rotationDeg % 360) + 360) % 360
					const desiredResidue = (360 - (targetCenter % 360)) % 360
					const deltaResidue = (desiredResidue - current + 360) % 360
					setAnimationDurationSec(4.2)
					setRotationDeg(rotationDeg + fullSpins * 360 + deltaResidue)
				} else {
					// Fallback: forward one full spin
					setAnimationDurationSec(4.2)
					setRotationDeg((prev) => prev + 360)
				}
			}
			setResultDone(true)
		},
		onError: (error: Error) => {
			showErrorToast(error.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
			setIsSpinning(false)
		},
	})

	const handleSpinClick = useCallback(() => {
		if (isSpinning || isPending || disabled) return

		resetSpinState()
		setIsSpinning(true)

		// Trigger backend spin and rotate precisely on result in onSuccess
		spin()
	}, [isPending, isSpinning, disabled, resetSpinState, spin])

	// If there's no wheel settings, display a message
	if (!wheelData.wheelSetting?.length) {
		return (
			<div className="flex flex-col items-center gap-2 p-8 text-center">
				<p className="text-lg text-muted-foreground">ไม่พบข้อมูลกงล้อ</p>
				<p className="text-sm text-muted-foreground">
					กรุณาตั้งค่ากงล้อก่อนใช้งาน
				</p>
			</div>
		)
	}

	// Use fallback component when showImageOnWheel is false
	if (!wheelData.showImageOnWheel) {
		return (
			<LuckyWheelFallback
				wheelData={wheelData}
				disabled={disabled}
				onSpinComplete={onSpinComplete}
			/>
		)
	}

	return (
		<div className="flex flex-col items-center gap-4">
			<div className="relative h-64 w-64">
				{/* Pointer at top center */}
				<div className="absolute -top-5 left-1/2 z-10 -translate-x-1/2">
					{wheelData.arrowWheelUrl ? (
						<img
							src={wheelData.arrowWheelUrl}
							alt="pointer"
							className="h-[55px] w-[35px] drop-shadow-lg"
						/>
					) : (
						<div className="h-0 w-0 border-b-8 border-l-4 border-r-4 border-b-red-500 border-l-transparent border-r-transparent drop-shadow-lg"></div>
					)}
				</div>

				<div className="absolute inset-0 flex items-center justify-center">
					<motion.div
						className="relative h-60 w-60 overflow-hidden rounded-full shadow-xl"
						style={{ willChange: 'transform' }}
						animate={{ rotate: rotationDeg }}
						transition={{
							type: 'tween',
							ease: 'easeInOut',
							duration: animationDurationSec,
						}}
						onAnimationComplete={() => {
							setAnimationDone(true)
						}}
					>
						{wheelData.wheelUrl ? (
							<img
								src={wheelData.wheelUrl}
								alt="lucky wheel"
								className="h-full w-full select-none object-contain"
								draggable={false}
							/>
						) : (
							<div className="flex h-full w-full items-center justify-center rounded-full border-2 border-gray-300 bg-gray-100">
								<p className="text-center text-sm text-gray-500">
									กรุณาอัพโหลด
									<br />
									รูปภาพกงล้อ
								</p>
							</div>
						)}

						{/* Prize labels overlay */}
						{SHOW_LABELS && wheelData.wheelUrl && (
							<div className="pointer-events-none absolute inset-0">
								{segments.map((seg, idx) => {
									const total = Math.max(segments.length, 1)
									const slice = 360 / total
									const angle =
										slice * ((idx - TOP_INDEX_AT_POINTER + total) % total)
									const radius = 92
									return (
										<div
											key={seg.id || idx}
											className="absolute left-1/2 top-1/2"
											style={{
												transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(-${radius}px) rotate(${-angle}deg)`,
											}}
										>
											<div className="rounded bg-white/80 px-1 py-0.5 text-center text-[15px] font-semibold">
												{seg.amount}
											</div>
										</div>
									)
								})}
							</div>
						)}
					</motion.div>
				</div>
			</div>

			<Button
				onClick={handleSpinClick}
				disabled={isSpinning || isPending || disabled}
				className="h-14 min-w-40 text-2xl font-bold"
			>
				{isSpinning || isPending ? 'กำลังหมุน...' : 'หมุน!'}
			</Button>

			{/* Latest prize display */}
			{!isSpinning && lastResultRef.current && (
				<div className="text-sm text-muted-foreground">
					รางวัลล่าสุด:{' '}
					<span className="font-medium text-primary">
						{lastResultRef.current.title} (+{lastResultRef.current.amount})
					</span>
				</div>
			)}
		</div>
	)
}
